"use client";

import { useRouter } from "next/navigation";
import React from "react";
import { FaCheck } from "react-icons/fa";

const DIGITAL_OCEAN_SPACES_CDN =
  "https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com";

// --- Check List Item (dark bg) ---
const CheckListItemDark = ({ text }: { text: string }) => (
  <li className="flex items-start gap-3">
    <span className="w-5 h-5 flex-shrink-0 rounded-full bg-purple-500 text-white flex items-center justify-center mt-0.5">
      <FaCheck size={9} />
    </span>
    <span className="text-purple-100 text-sm sm:text-base leading-relaxed">
      {text}
    </span>
  </li>
);

// --- Check List Item (light bg) ---
const CheckListItemLight = ({ text }: { text: string }) => (
  <li className="flex items-start gap-3">
    <span className="w-5 h-5 flex-shrink-0 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mt-0.5">
      <FaCheck size={9} />
    </span>
    <span className="text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
      {text}
    </span>
  </li>
);

export default function PropertyListingPage() {
  const router = useRouter();

  const whyBenefits = [
    "No registration fees",
    "100% local platform",
    "Simple, transparent pricing",
  ];

  const whatYouGet = [
    "Direct offers to guests",
    "Better visibility",
    "Strong local marketing",
  ];

  const signUpFeatures = [
    "Fast and free registration",
    "Choose between Instant Booking or Request to Book",
    "Secure payment system",
    "Designed for both resorts and budget stays",
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <main className="flex-1 w-full">
        {/* ===== HERO — Dark purple, device image right ===== */}
        <section className="bg-purple-900 pt-28 md:pt-32 pb-5 px-4 sm:px-6 lg:px-12 ">
          {" "}
          <div className="max-w-6xl mx-auto flex flex-col-reverse lg:flex-row items-center gap-10 lg:gap-16">
            {/* LEFT */}
            <div className="flex-1 lg:text-left">
              <span className="inline-block bg-purple-800 text-purple-200 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-5">
                🇱🇰 For Sri Lankan Hoteliers
              </span>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-3">
                List Your Property on{" "}
                <span className="text-purple-400 text-left">LilyLanka</span>
              </h1>
              <p className="text-purple-300 text-base sm:text-lg font-semibold mb-1">
                A Smarter Way to Get Bookings
              </p>
              <p className="text-purple-200 text-sm sm:text-base mb-8 max-w-lg  leading-relaxed lg:text-left">
                Stop giving away high commissions. Start growing with a platform
                built for you.
              </p>

              {/* Why Lily Lanka */}
              <div className="mb-8">
                <p className="text-purple-400 text-xs font-bold tracking-widest uppercase mb-3">
                  Why Lily Lanka
                </p>
                <ul className="space-y-2.5 max-w-xs text-left">
                  {whyBenefits.map((item, i) => (
                    <CheckListItemDark key={i} text={item} />
                  ))}
                </ul>
              </div>

              {/* Commission badge */}
              <div className="inline-flex items-center gap-4 bg-purple-900/70 border border-purple-600 rounded-xl px-5 py-3 mb-8 text-left">
                <div>
                  <p className="text-purple-400 text-xs font-bold uppercase tracking-widest mb-0.5">
                    Our Commission
                  </p>
                  <p className="text-white text-2xl font-extrabold leading-none">
                    Flat 12% Only
                  </p>
                  <p className="text-purple-300 text-xs mt-0.5">
                    No hidden charges.
                  </p>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center justify-start lg:justify-start gap-3">
                <button
                  onClick={() => router.push("/partner/sign-in-property")}
                  className="bg-purple-500 hover:bg-purple-400 text-white font-semibold py-3 px-8 rounded-lg transition-colors w-full sm:w-auto cursor-pointer text-sm"
                >
                  Sign In Partner Account
                </button>
                <button
                  onClick={() => router.push("/partner/sign-up-form")}
                  className="bg-transparent hover:bg-purple-700 text-purple-300 hover:text-white font-semibold py-3 px-8 rounded-lg border border-purple-500 transition-colors w-full sm:w-auto cursor-pointer text-sm"
                >
                  Create Partner Account
                </button>
              </div>
            </div>

            {/* RIGHT: Device image */}
            <div className="flex-1 flex items-center justify-center w-full max-w-sm lg:max-w-none">
              <div className="relative w-full max-w-md">
                <div className="absolute inset-0 bg-purple-500 opacity-20 blur-3xl rounded-full" />
                <img
                  src={`${DIGITAL_OCEAN_SPACES_CDN}/Site/images/logo/logo_white1.png`}
                  alt="Lily Lanka partner dashboard on laptop"
                  className="relative w-full object-contain drop-shadow-2xl"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const next = e.currentTarget
                      .nextElementSibling as HTMLElement | null;
                    if (next) next.style.display = "flex";
                  }}
                />
                {/* Fallback */}
                <div
                  className="relative w-full aspect-video bg-[#2a0f4a] rounded-2xl border border-purple-700 flex-col gap-3"
                  style={{ display: "none" }}
                >
                  <div className="flex flex-col items-center justify-center h-full gap-3">
                    <div className="w-20 h-20 rounded-2xl bg-purple-700 flex items-center justify-center">
                      <span className="text-3xl">🏨</span>
                    </div>
                    <p className="text-purple-300 text-sm font-bold">
                      Lily Lanka Partner Dashboard
                    </p>
                    <p className="text-purple-500 text-xs">
                      A Smarter Way to Get Bookings
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== WHAT YOU GET + IDENTITY ===== */}
        <section className="bg-white dark:bg-gray-900 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <p className="text-purple-500 dark:text-purple-400 text-xs font-bold tracking-widest uppercase mb-4">
                What You Get
              </p>
              <ul className="space-y-3">
                {whatYouGet.map((item, i) => (
                  <CheckListItemLight key={i} text={item} />
                ))}
              </ul>
            </div>
            <div className="bg-purple-50 dark:bg-purple-950 rounded-2xl p-6 border border-purple-100 dark:border-purple-800">
              <p className="text-purple-500 text-xs font-bold tracking-widest uppercase mb-3">
                Our Identity
              </p>
              <p className="text-gray-800 dark:text-white font-bold text-base sm:text-lg mb-2">
                Built by Sri Lankans, for Sri Lankans, for Sri Lanka
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                Sri Lanka's first fully operational SLTDA-registered web &amp;
                app platform for hoteliers.
              </p>
            </div>
          </div>
        </section>

        {/* ===== LIMITED EARLY ACCESS CTA ===== */}
        {/* <section className="bg-[#1a0533] py-14 px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-2xl mx-auto">
            <span className="inline-block bg-purple-800 text-purple-200 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-2">
              Limited Early Access
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
              Join Now and Grow With Us From the Start
            </h2>
            <p className="text-purple-300 text-sm sm:text-base mb-8">
              Earn more. Keep more. Grow faster.
            </p>
          </div>
        </section> */}

        {/* ===== REGISTER FREE ===== */}
        <section className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">
              Register for Free — Start Hosting in Minutes
            </h2>
            <ul className="space-y-4 max-w-md mx-auto text-left mb-10">
              {signUpFeatures.map((item, i) => (
                <CheckListItemLight key={i} text={item} />
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => router.push("/partner/sign-in-property")}
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors w-full sm:w-auto cursor-pointer text-sm"
              >
                Sign In Partner Account
              </button>
              <button
                onClick={() => router.push("/partner/sign-up-form")}
                className="bg-white hover:bg-purple-600 text-purple-600 hover:text-white font-semibold py-3 px-8 rounded-lg border border-purple-300 transition-colors w-full sm:w-auto cursor-pointer text-sm"
              >
                Create Partner Account
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
